import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  CircularProgress,
  Paper,
  Avatar,
} from '@mui/material';
import {
  BarChart,
  People,
  Dns,
  Dashboard,
  Storage,
  Task,
} from '@mui/icons-material';
import { useStatisticsApi } from '../../hooks/useStatisticsApi';
import { TicketStatisticsCard } from '../../components/TicketStatisticsCard';
import {
  StatisticsResponseDto,
  ClientStatisticsResponseDto,
} from '../../generated/data-contracts';

const StatCard = ({ icon, title, value, color }) => (
  <Card
    sx={{
      display: 'flex',
      alignItems: 'center',
      p: 2,
      backgroundColor: `${color}10`,
      color: `${color}`,
    }}
  >
    <Avatar sx={{ bgcolor: color, color: 'white', mr: 2 }}>{icon}</Avatar>
    <Box>
      <Typography variant='h6'>{value}</Typography>
      <Typography variant='subtitle2'>{title}</Typography>
    </Box>
  </Card>
);

export default function StatisticPage() {
  const [generalStats, setGeneralStats] =
    useState<StatisticsResponseDto | null>(null);
  const [clientStats, setClientStats] = useState<
    ClientStatisticsResponseDto[] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const { getGeneralStatistics, getClientStatistics, api } = useStatisticsApi();

  useEffect(() => {
    const fetchData = async () => {
      if (!api) return;
      setLoading(true);
      const generalData = await getGeneralStatistics();
      const clientData = await getClientStatistics();
      setGeneralStats(generalData);
      setClientStats(clientData);
      setLoading(false);
    };

    fetchData();
  }, [api, getGeneralStatistics, getClientStatistics]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant='h4'
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <BarChart sx={{ fontSize: 40, mr: 1 }} /> Estadísticas Generales
      </Typography>

      {generalStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<People />}
              title='Usuarios'
              value={generalStats.totalUsers}
              color='#1976d2'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Dns />}
              title='Clientes'
              value={clientStats?.length}
              color='#388e3c'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Storage />}
              title='Variables SCADA'
              value={generalStats.totalScadaVariables}
              color='#f57c00'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Task />}
              title='Tareas'
              value={generalStats.totalTasks}
              color='#d32f2f'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Dashboard />}
              title='Dashboards'
              value={generalStats.totalDashboards}
              color='#7b1fb9'
            />
          </Grid>
        </Grid>
      )}

      <Typography
        variant='h5'
        gutterBottom
        sx={{ mt: 4, display: 'flex', alignItems: 'center' }}
      >
        <People sx={{ fontSize: 30, mr: 1 }} /> Estadísticas por Cliente
      </Typography>

      {clientStats && clientStats.length > 0 ? (
        <Grid container spacing={3}>
          {clientStats.map(client => (
            <Grid item xs={12} md={6} lg={4} key={client.clientId}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant='h6' gutterBottom>
                  {client.clientName}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <StatCard
                      icon={<Storage />}
                      title='Variables SCADA'
                      value={client.scadaVariables}
                      color='#f57c00'
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StatCard
                      icon={<Dashboard />}
                      title='Dashboards'
                      value={client.dashboards}
                      color='#7b1fb9'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StatCard
                      icon={<Task />}
                      title='Tareas'
                      value={client.tasks}
                      color='#d32f2f'
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography>No hay estadísticas por cliente para mostrar.</Typography>
      )}

      {/* Estadísticas de Tickets */}
      <Box mt={4}>
        <TicketStatisticsCard />
      </Box>
    </Box>
  );
}
